import { useRef, useEffect } from 'react';

/**
 * Tracks whether the component is still mounted so async callbacks can
 * bail out before touching state on an unmounted tree.
 *
 * Usage:
 * ```tsx
 * const mounted = useMountedRef();
 * useEffect(() => {
 *   doAsyncWork().then((data) => {
 *     if (!mounted.current) return;
 *     setState(data);
 *   });
 * }, []);
 * ```
 *
 * Returns a stable ref whose `.current` is `true` while mounted and
 * flips to `false` on unmount.
 */
export function useMountedRef() {
  const mounted = useRef(true);
  useEffect(() => {
    // Set true in the effect body (not just via useRef init) so a remount restores it.
    // Under React 18 StrictMode the effect runs mount→cleanup→setup in dev: relying only
    // on useRef(true) would leave `current` stuck at false after that first cleanup.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}
